import { Component, OnInit, ChangeDetectorRef } from '@angular/core';
import { ActivatedRoute, Router } from '@angular/router';
import { ClientesService } from '../../services/clientes.service';
import { HelpersService } from '../../services/tools/helpers.service';
import { PaisService } from '../../services/pais.services';

@Component({
  selector: 'app-cliente-form',
  templateUrl: './cliente-form.component.html',
  styleUrls: ['./cliente-form.component.scss']
})
export class ClienteFormComponent implements OnInit {

  public paises: any[] = [];
  public idCliente: number | null = null;
  public isLoading: boolean = false;

  // Catálogo estático de estados para uso de la aplicación
  public listaEstados: string[] = [
    'AGUASCALIENTES', 'BAJA CALIFORNIA', 'BAJA CALIFORNIA SUR', 'CAMPECHE', 'COAHUILA',
    'COLIMA', 'CHIAPAS', 'CHIHUAHUA', 'CIUDAD DE MÉXICO', 'DURANGO', 'GUANAJUATO',
    'GUERRERO', 'HIDALGO', 'JALISCO', 'MÉXICO', 'MICHOACÁN', 'MORELOS', 'NAYARIT',
    'NUEVO LEÓN', 'OAXACA', 'PUEBLA', 'QUERÉTARO', 'QUINTANA ROO', 'SAN LUIS POTOSÍ',
    'SINALOA', 'SONORA', 'TABASCO', 'TAMAULIPAS', 'TLAXCALA', 'VERACRUZ', 'YUCATÁN', 'ZACATECAS'
  ];

  // Estructura de archivos organizada
  public archivos: any = {
    ine_anverso: null,
    ine_reverso: null,
    comp_domicilio: null,
    constancia_fiscal: null,
    curp_pdf: null,
    estado_cuenta: null
  };

  public previewArchivos: any = {};
  public errors: any = [];

  // Objeto maestro original sin omisiones
  public cliente: any = {
    tipo_persona: '',
    nombre: '',
    paterno: '',
    materno: '',
    genero: '',
    curp: '',
    rfc: '',
    fecha_nacimiento: '',
    identificacion_tipo: '',
    cic: '',
    ocr: '',
    clave_elector: '',
    identificacion_serie: '',
    identificacion_vencimiento: '',
    pais_nacimiento: '',
    nacionalidad: '',
    email: '',
    telefono_celular: '',
    telefono_casa: '',
    telefono_oficina: '',

    pais: '',
    estado: '',
    municipio: '',
    cp: '',
    calle: '',
    num_exterior: '',
    num_interior: '',
    colonia: '',

    profesion: '',
    ocupacion: '',
    puesto: '',
    actividad: '',
    giro_negocio: '',
    nombre_negocio: '',
    descripcion_actividades: '',
    representante_legal: '',
    domicilio_fiscal: '',

    contacto_nombre: '',
    contacto_puesto: '',
    contacto_correo: '',
    contacto_telefono: '',

    pais_laboral: '',
    estado_laboral: '',
    municipio_laboral: '',
    cp_laboral: '',
    calle_laboral: '',
    num_exterior_laboral: '',
    num_interior_laboral: '',
    colonia_laboral: '',

    tipo_control: '',
    razon_social: '',
    fecha_constitucion: '',
    pais_nacionalidad: '',
    giro_mercantil: '',
    representantes: []
  };

  constructor(
    private route: ActivatedRoute,
    private router: Router,
    private service: ClientesService,
    private helperService: HelpersService,
    private staticPaisService: PaisService,
    public cdRef: ChangeDetectorRef
  ) { }

  ngOnInit(): void {
    this.cargarPaises();
    this.route.params.subscribe(params => {
      if (params['id']) {
        this.idCliente = +params['id'];
        this.getCliente(this.idCliente);
      }
    });
  }

  private cargarPaises(): void {
    this.staticPaisService.getPaises().subscribe({
      next: (data) => this.paises = data,
      error: (err) => console.error('Error de catálogos', err)
    });
  }

  public getCliente(id: number): void {
    this.isLoading = true;
    this.service.getById(id).subscribe({
      next: (response: any) => {
        const data = response.json ? response.json().content : (response.content || response);
        let formValues = { ...this.cliente };
        this.archivos = {};
        this.previewArchivos = {};

        // Mapeo inteligente de datos planos
        Object.keys(data).forEach(key => {
          if (!['perfil_fisico', 'perfil_moral', 'domicilios', 'documentos'].includes(key)) {
            formValues[key] = data[key];
          }
        });

        if (data.perfil_fisico) Object.assign(formValues, data.perfil_fisico);
        if (data.perfil_moral) Object.assign(formValues, data.perfil_moral);

        // Mapeo completo y profesional de colecciones de Domicilios sin omitir llaves
        if (Array.isArray(data.domicilios)) {
          data.domicilios.forEach((dom: any) => {
            const suffix = dom.tipo_domicilio === 'LABORAL' ? '_laboral' : '';
            const camposDom = ['pais', 'estado', 'municipio', 'cp', 'calle', 'num_exterior', 'num_interior', 'colonia'];
            camposDom.forEach(campo => {
              if (dom[campo] !== undefined && dom[campo] !== null) {
                formValues[`${campo}${suffix}`] = dom[campo];
              }
            });
          });
        }

        // Mapeo estructurado de documentos binarios
        if (Array.isArray(data.documentos)) {
          data.documentos.forEach((doc: any) => {
            if (String(doc.status) === '1') {
              formValues[doc.tipo_documento] = doc.ruta_final;
              formValues[`${doc.tipo_documento}_id`] = doc.id;

              this.previewArchivos[doc.tipo_documento] = {
                url: doc.ruta_final,
                nombreOriginal: doc.nombre_original,
                extension: doc.extension
              };
            }
          });
        }

        this.cliente = formValues;
        this.isLoading = false;
      },
      error: (err) => {
        this.isLoading = false;
        this.helperService.httpGlobalError(err);
      }
    });
  }

  public cambiarTipoPersona(): void {
    if (this.cliente.tipo_persona === 'MORAL' && (!this.cliente.representantes || this.cliente.representantes.length === 0)) {
      this.addRepresentante();
    }
  }

  public addRepresentante(): void {
    if (!this.cliente.representantes) {
      this.cliente.representantes = [];
    }
    // Añade el esquema idéntico exacto para evitar inconsistencias en el backend
    this.cliente.representantes.push({
      nombre: '', paterno: '', materno: '', genero: '', curp: '', rfc: '', fecha_nacimiento: '',
      identificacion_tipo: '', cic: '', ocr: '', clave_elector: '', identificacion_serie: '',
      identificacion_vencimiento: '', firma_electronica_serie: '', pais_nacimiento: 'MEXICO',
      nacionalidad: 'MEXICANA', email: '', telefono_celular: '', telefono_casa: '', telefono_oficina: '',
      pais: 'MEXICO', estado: '', municipio: '', cp: '', calle: '', num_exterior: '', num_interior: '', colonia: '',
      profesion: '', ocupacion: '', puesto: '', actividad: '', giro_negocio: '', nombre_negocio: '',
      descripcion_actividades: '', representante_legal: '', domicilio_fiscal: '',
      contacto_nombre: '', contacto_puesto: '', contacto_correo: '', contacto_telefono: '',
      pais_laboral: 'MEXICO', estado_laboral: '', municipio_laboral: '', cp_laboral: '',
      calle_laboral: '', num_exterior_laboral: '', num_interior_laboral: '', colonia_laboral: ''
    });
  }

  public removeRepresentante(index: number): void {
    this.cliente.representantes.splice(index, 1);
  }

  // Delegación central de eventos de archivos de componentes hijos
  public handleChildFile(event: { file: File, campo: string }): void {
    this.archivos[event.campo] = event.file;
  }

  public downloadFile(idFile: any): void {
    this.service.getFile(idFile).subscribe({
      next: (response) => {
        const fileUrl = window.URL.createObjectURL(response.blob());
        window.open(fileUrl);
      },
      error: (err) => {
        this.helperService.httpGlobalError(err);
        if (err.status === 400) {
          this.setBackendErrors(err.json().content);
          this.helperService.notification('ERROR', 'No se completó la acción.', 'error');
        }
      }
    });
  }

  private setBackendErrors(errors: any): void {
    let temp_errors: { [key: string]: string } = {};
    Object.keys(errors).forEach((key) => {
      temp_errors[key] = errors[key][0];
    });
    this.errors = temp_errors;
  }

  public save(): void {
    this.isLoading = true;
    const formData = new FormData();
    let payload = JSON.parse(JSON.stringify(this.cliente));

    // 1. Limpieza de seguridad para evitar enviar datos basura cruzados
    if (payload.tipo_persona === 'FISICA') {
      payload.representantes = [];
      payload.tipo_control = '';
      payload.razon_social = '';
      payload.fecha_constitucion = '';
      payload.giro_mercantil = '';
    }

    // 2. Formateador de fechas seguro para MySQL/Backend (YYYY-MM-DD -> DD/MM/YYYY)
    const formatDateForDB = (date: string) => {
      // Si ya tiene el formato YYYY-MM-DD, devuélvelo tal cual.
      // Solo asegúrate de que no esté vacío.
      if (!date) return null;

      // Si por alguna razón el picker envía DD/MM/YYYY, entonces sí inviértelo
      if (date.includes('/')) {
        const [d, m, y] = date.split('/');
        return `${y}-${m}-${d}`;
      }

      return date; // Ya viene como YYYY-MM-DD
    };

    payload.fecha_nacimiento = formatDateForDB(payload.fecha_nacimiento);
    payload.fecha_constitucion = formatDateForDB(payload.fecha_constitucion);
    payload.identificacion_emision = formatDateForDB(payload.identificacion_emision);
    payload.fecha_emision_comprobante = formatDateForDB(payload.fecha_emision_comprobante);
    payload.fecha_fin_cargo = formatDateForDB(payload.fecha_fin_cargo);

    if (payload.representantes && payload.representantes.length > 0) {
      payload.representantes.forEach((r: any) => {
        r.fecha_nacimiento = formatDateForDB(r.fecha_nacimiento);
      });
    }

    // 3. Transformación profesional de JSON a FormData (Soporta arrays y campos vacíos)
    this.buildFormData(formData, payload);

    // 4. Adjuntar archivos globales y de representantes de forma dinámica
    for (let key in this.archivos) {
      if (this.archivos[key]) {
        formData.append(key, this.archivos[key]);
      }
    }

    // 5. CRÍTICO PARA LUMEN: Method Spoofing en actualizaciones
    if (this.idCliente) {
      // Le indicamos a Lumen que procese esto como un PUT, aunque viaje por POST
      formData.append('_method', 'PUT');
    }

    // 6. Ejecución del servicio correspondiente
    const request = this.idCliente
      ? this.service.update(this.idCliente, formData)
      : this.service.create(formData);

    request.subscribe({
      next: () => {
        this.isLoading = false;
        this.helperService.notification('Éxito', 'Registro guardado correctamente', 'success');
        this.router.navigate(['/clientes/listado']);
      },
      error: (err) => {
        this.isLoading = false;
        this.helperService.httpGlobalError(err);

        if (err.status === 400) {
          // Extraemos el error dependiendo de cómo el interceptor formatea la respuesta
          const errorData = err.error ? err.error.content : (err.json ? err.json().content : null);

          if (errorData) {
            this.setBackendErrors(errorData);
            this.helperService.notification('Atención', 'Verifica los campos obligatorios.', 'warning');
          }
        }
      }
    });
  }

  /**
   * Utilidad Profesional: Transforma un objeto complejo en un FormData compatible con PHP.
   * Maneja correctamente arrays de objetos y fuerza el envío de campos vacíos.
   */
  private buildFormData(formData: FormData, data: any, parentKey?: string): void {
    if (data && typeof data === 'object' && !(data instanceof Date) && !(data instanceof File)) {
      Object.keys(data).forEach(key => {
        const value = data[key];
        const formKey = parentKey ? `${parentKey}[${key}]` : key;

        if (Array.isArray(value)) {
          // Formateo estricto para arrays requeridos en PHP (ej. representantes[0][nombre])
          if (value.length === 0) {
            formData.append(`${formKey}[]`, '');
          } else {
            value.forEach((item, index) => {
              this.buildFormData(formData, item, `${formKey}[${index}]`);
            });
          }
        } else if (value !== null && typeof value === 'object') {
          // Recursión natural para objetos anidados
          this.buildFormData(formData, value, formKey);
        } else {
          // CRÍTICO: Los campos nulos o vacíos SE DEBEN ENVIAR para que el validador del backend responda adecuadamente
          let finalValue = (value === null || value === undefined) ? '' : value;

          if (typeof finalValue === 'boolean') {
            finalValue = finalValue ? '1' : '0';
          }

          formData.append(formKey, finalValue.toString());
        }
      });
    }
  }
}


// WEBPACK FOOTER //
// ./src/app/clientes/cliente-form/cliente-form.component.ts